import React, { FC, useMemo, useState } from 'react'

import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { ErrorMessage, Form, Formik, getIn } from 'formik'
import { pick, startCase } from 'lodash'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'

import { defaultMailingAddress } from './constants'
import {
  checkMailingAddress,
  getCoApplicant,
  getCreditReportType,
  getFormattedApplicants,
  handleCasesWithUserModal,
} from './utils'
import { validationSchema } from './validationSchema'
import bgImage from '@/assets/img/pull-credit-bg.svg'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { SelectField } from '@/components/FormikInputs'
import LoadingIcon from '@/components/LoadingIcon'
import { graphqlApi } from '@/graphql'
import { Application } from '@/graphql/generated'
import { KeyLabel } from '@/key-components/KeyLabel'
import KeyMaskedSSNInput from '@/key-components/KeyMaskedSSNInput'
import { KeySwitch } from '@/key-components/KeySwitch'
import { KeyTabNavigation } from '@/key-components/KeyTabNavigation'
import { KeyInputField } from '@/key-components/formik/KeyInputField'
import { useCreateAddressMutation, useUpdateAddressMutation } from '@/services/address'
import { useAddCoApplicantMutation } from '@/services/profile'
import { usePullCreditMutation } from '@/services/v2-application/applicationProfile'
import { ApplicationProfile } from '@/types/unlock'
import { getLatestValue } from '@/utils'

export type PullCreditModalProps = {
  application: Application
  applicant: ApplicationProfile
  otherApplicants: ApplicationProfile[]
  onClose: () => void
  applicationId: string
}

const tabs = {
  order_new: {
    title: 'Order New Report',
  },
  retrieve_existing: {
    title: 'Retrieve Existing Report',
  },
  upgrade_add_spouse: {
    title: 'Upgrade Report / Add Spouse',
  },
  unmerge: {
    title: 'Unmerge Joint Report',
  },
}

const modalTitle = (
  <p>
    <b>Credit Report Services | </b>{' '}
    <span className='pl-1'>Configure Credit Report Order Type</span>
  </p>
)

export const PullCreditModal: FC<PullCreditModalProps> = ({
  applicant,
  otherApplicants,
  application,
  onClose,
  applicationId,
}) => {
  const router = useRouter()
  const { id: applicationExternalID } = router.query
  const dispatch = useAppDispatch()
  const pullCreditModalAction = useAppSelector((state) => state.general.pullCreditModalAction)
  const pullCreditModalUser = useAppSelector((state) => state.general.pullCreditModalUser)
  const [isAddigCoApp, setIsAddingCoApp] = useState(false)

  const formattedApplicants = getFormattedApplicants([applicant, ...otherApplicants], true)

  const applicantsOptions = useMemo(() => {
    return formattedApplicants.map((currentApplicant, index) => {
      return {
        value: index,
        label: `${currentApplicant.full_name}`,
        uuid: currentApplicant.applicationProfileUUID,
      }
    })
  }, [formattedApplicants])

  const creditReportOptions = useMemo(() => {
    const creditReports = formattedApplicants.reduce((combinedCreditReports, currentApplicant) => {
      return combinedCreditReports.concat(currentApplicant.credit_reports)
    }, [])
    const uniqueCreditReports = new Map()
    creditReports.forEach((object) => {
      uniqueCreditReports.set(object.external_id, object)
    })
    return [...Array.from(uniqueCreditReports.values())].map((cr) => {
      return {
        value: cr.file_number,
        label: `${cr.file_number}`,
        applicants: cr.applicants,
        joint: cr.is_joint,
      }
    })
  }, [formattedApplicants])

  const jointCreditReportOptions = useMemo(() => {
    return creditReportOptions.filter((report) => report.joint)
  }, [creditReportOptions])

  const singleCreditReportOptions = useMemo(() => {
    return creditReportOptions.filter((report) => !report.joint)
  }, [creditReportOptions])

  const coApplicantsOptions = useMemo(() => applicantsOptions.slice(1), [formattedApplicants])
  const userModalIndex = useMemo(
    () =>
      formattedApplicants.findIndex(
        (currentApplicant) => currentApplicant.uuid === pullCreditModalUser
      ),
    []
  )
  const [addCoApplicant, { isLoading: isLoadingCreatingProfile }] = useAddCoApplicantMutation()
  const [pullCredit, { error: pullCreditError, isLoading: isPullingCredit, isError, isSuccess }] =
    usePullCreditMutation()
  const [updateAddress, { isLoading: isLoadingUpdateAddress }] = useUpdateAddressMutation()
  const [createAddress, { isLoading: isLoadingCreteAddress }] = useCreateAddressMutation()

  const errorMessage = (pullCreditError as any)?.data?.detail ?? null

  const initialValues = useMemo(() => {
    const latestCreditReport = getLatestValue(applicant.credit_reports)
    return {
      action: pullCreditModalAction,
      single_or_joint: getCreditReportType(userModalIndex !== -1 ? latestCreditReport : null),
      file_number: userModalIndex !== -1 ? latestCreditReport?.file_number : '',
      creditReportIsJoint: false,
      mailing_address: {
        street: applicant.profile.mailing_address?.street || '',
        unit_number: applicant.profile.mailing_address?.unit_number || '',
        external_id: applicant.profile.mailing_address?.external_id,
        uuid: applicant.profile.mailing_address?.uuid,
        city: applicant.profile.mailing_address?.city || '',
        state: applicant.profile.mailing_address?.state || '',
        zip_code: applicant.profile.mailing_address?.zip_code || '',
        profile: applicant.profile.id,
      },
      applicants: formattedApplicants,
      applicant: {
        uuid: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        suffix: '',
        social_security_number: '',
        mailing_address: {
          street: '',
          unit_number: '',
          city: '',
          state: '',
          zip_code: '',
        },
      },
      coApplicant: {
        uuid: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        suffix: '',
        social_security_number: '',
        mailing_address: {
          street: '',
          unit_number: '',
          city: '',
          state: '',
          zip_code: '',
        },
      },
      selectedApplicant:
        userModalIndex !== -1
          ? getCoApplicant(
              userModalIndex,
              formattedApplicants,
              creditReportOptions,
              latestCreditReport
            )
          : null,
      newApplicant: {
        first_name: '',
        last_name: '',
        social_security_number: '',
      },
    }
  }, [application, applicant])

  const handleCreditPull = async (values) => {
    let profiles = [
      {
        submited: values.applicants[values.selectedApplicant],
        initial: initialValues.applicants[values.selectedApplicant],
        newProfile: false,
      },
    ]

    const creatingNewApplicant =
      (values.action === 'upgrade_add_spouse' &&
        values.single_or_joint === 'joint' &&
        (values.selectedApplicant === null || values.selectedApplicant === undefined)) ||
      (values.action === 'order_new' &&
        values.single_or_joint === 'joint' &&
        (values.selectedApplicant === null || values.selectedApplicant === undefined))

    if (creatingNewApplicant) {
      profiles = [
        {
          submited: values.newApplicant,
          initial: initialValues.newApplicant,
          newProfile: true,
        },
      ]
    }

    if (values.single_or_joint === 'joint') {
      profiles.push({
        submited: values.applicants[0],
        initial: initialValues.applicants[0],
        newProfile: false,
      })
    }

    if (checkMailingAddress(values.single_or_joint, initialValues, values)) {
      if (!values.mailing_address.uuid) {
        const profile =
          values.single_or_joint === 'joint'
            ? applicant.profile.id
            : values.applicants[values.selectedApplicant].id

        await createAddress({
          address: { ...values.mailing_address, profile },
        })
      } else {
        await updateAddress(values.mailing_address)
      }
    }

    await pullCredit({
      ...pick(values, ['action', 'single_or_joint', 'file_number']),
      applicationId: application.uuid,
      applicantProfileId: values.applicants[values.selectedApplicant].applicationProfileUUID,
      coApplicantProfileId: values.applicants[values.selectedCoApplicant]?.applicationProfileUUID,
    })
    // TODO: remove when we have Graphql WS invalidation
    // Explicit Graphql Invalidation, as implicit invalidation on service is crashing.
    dispatch(graphqlApi.util.invalidateTags(['gql:applicationOfferCalcs', 'gql:application']))
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleCreditPull}
      validationSchema={validationSchema}
      validateOnMount
    >
      {({
        values,
        isValid,
        setFieldValue,
        validateForm,
        errors,
        touched,
        validateField,
        setFieldTouched,
      }) => {
        const { action, single_or_joint, applicants, selectedApplicant, file_number } = values

        return (
          <div
            id='overlay'
            className='top-0 left-0 fixed w-screen h-screen bg-gray-400/60 flex items-center justify-center'
            style={{ zIndex: 999 }}
          >
            <div
              className='flex flex-col gap-4 w-[960px] bg-white rounded-lg pb-4'
              style={{ maxHeight: '90vh' }}
            >
              <div className='flex justify-between border-b-2 pl-8 p-4 bg-sky-200 text-black rounded-tl-lg rounded-tr-lg'>
                <h5>{modalTitle}</h5>
                <FontAwesomeIcon
                  icon={faXmark}
                  width={12}
                  className='cursor-pointer'
                  onClick={() => onClose()}
                />
              </div>

              {isPullingCredit ||
              isLoadingUpdateAddress ||
              isLoadingCreteAddress ||
              isLoadingCreatingProfile ? (
                <div className='px-8 flex flex-col items-center'>
                  <LoadingIcon
                    width={80}
                    height={80}
                  />
                  <p className='my-4'>Loading...</p>
                </div>
              ) : isError ? (
                <div className='px-8 flex flex-col items-center'>
                  <XCircleIcon
                    width={80}
                    stroke={'red'}
                  />
                  <h4 className='font-bold my-4'>
                    {errorMessage ? startCase(errorMessage) : 'Credit Report Cannot Be Found'}
                  </h4>
                  <p className='my-4'>
                    Please confirm that the name(s), SSN and current address are correct and try
                    again.
                  </p>
                </div>
              ) : isSuccess ? (
                <div className='px-8 flex flex-col items-center'>
                  <CheckCircleIcon
                    width={80}
                    stroke={'green'}
                  />
                  <h4 className='font-bold my-4'>Credit Report Retrieved</h4>
                </div>
              ) : (
                <div className='px-8 text-sm'>
                  <Form>
                    <KeyTabNavigation
                      tabs={tabs}
                      activeTab={values.action}
                      onTabClick={(tab) => {
                        setFieldValue('action', tab)
                        setFieldValue('file_number', '')
                        setFieldValue('creditReportIsJoint', false)
                        setIsAddingCoApp(false)
                        if (
                          tab === 'unmerge' &&
                          single_or_joint === 'single' &&
                          selectedApplicant !== '' &&
                          selectedApplicant !== null &&
                          selectedApplicant !== undefined &&
                          applicants[selectedApplicant] &&
                          applicants[selectedApplicant].uuid === applicant.profile.uuid
                        ) {
                          setFieldValue('selectedApplicant', null)
                        }
                        if (pullCreditModalUser) {
                          handleCasesWithUserModal(
                            applicants,
                            selectedApplicant,
                            applicant,
                            setFieldValue,
                            dispatch,
                            tab,
                            single_or_joint
                          )
                        }
                        if (tab === 'upgrade_add_spouse') {
                          setFieldValue('single_or_joint', 'joint')
                          setFieldValue(
                            'mailing_address',
                            applicant.profile.mailing_address ?? defaultMailingAddress
                          )
                          if (pullCreditModalUser) {
                            handleCasesWithUserModal(
                              applicants,
                              selectedApplicant,
                              applicant,
                              setFieldValue,
                              dispatch,
                              action,
                              'joint'
                            )
                          } else {
                            setFieldValue('selectedApplicant', null)
                          }
                          setTimeout(() => {
                            // validateField('single_or_joint')
                          }, 0)
                        }
                        if (tab === 'unmerge') {
                          setFieldValue('single_or_joint', 'joint')
                          if (pullCreditModalUser) {
                            handleCasesWithUserModal(
                              applicants,
                              selectedApplicant,
                              applicant,
                              setFieldValue,
                              dispatch,
                              action,
                              'single'
                            )
                          } else {
                            setFieldValue('selectedApplicant', null)
                          }
                          setTimeout(() => {
                            // validateField('single_or_joint')
                          }, 0)
                        }
                        setTimeout(() => {
                          validateForm()
                        }, 0)
                      }}
                    />
                    {!action ? (
                      <div className='h-96 flex items-center justify-center flex-col'>
                        <Image
                          src={bgImage}
                          alt='Pull Credit Modal'
                        ></Image>
                        <div className='text-gray-400'>
                          Select a <b>Report Type</b> to begin
                        </div>
                      </div>
                    ) : (
                      <div
                        className='overflow-auto px-6 pt-4'
                        style={{ maxHeight: '60vh' }}
                      >
                        {!!getIn(errors, 'action') && (
                          <div className='text-error'>{getIn(errors, 'action')}</div>
                        )}
                        <div className='flex gap-2 items-center w-max ml-auto'>
                          <KeyLabel
                            disabled={applicantsOptions.length <= 1}
                            className={
                              single_or_joint === 'single' ? 'text-gray-400 font-semibold' : ''
                            }
                          >
                            Single Report
                          </KeyLabel>
                          <KeySwitch
                            checked={single_or_joint === 'joint'}
                            disabled={
                              !['order_new', 'retrieve_existing'].includes(action) ||
                              applicantsOptions.length <= 1
                            }
                            onClick={() => {
                              const value = single_or_joint === 'joint' ? 'single' : 'joint'
                              setFieldValue('single_or_joint', value)
                              if (pullCreditModalUser) {
                                handleCasesWithUserModal(
                                  applicants,
                                  selectedApplicant,
                                  applicant,
                                  setFieldValue,
                                  dispatch,
                                  action,
                                  value
                                )
                              }
                              setTimeout(() => {
                                // validateField('single_or_joint')
                              }, 0)
                            }}
                          />
                          <KeyLabel
                            className={
                              single_or_joint === 'joint' ? 'text-gray-400 font-semibold' : ''
                            }
                          >
                            Joint Report
                          </KeyLabel>
                        </div>
                        <ErrorMessage
                          component='span'
                          name='single_or_joint'
                          className='text-error'
                        />
                        {action !== 'order_new' && (
                          <div className='flex flex-col gap-2 mb-4'>
                            <label
                              htmlFor='file_number'
                              className='block w-full text-sm text-gray-400'
                            >
                              File Number
                            </label>
                            <KeyInputField
                              placeholder='XXXXXXX'
                              name='file_number'
                              type='number'
                              className='w-full max-w-full'
                            />
                          </div>
                        )}
                        {(action === 'order_new' || (action !== 'order_new' && !!file_number)) && (
                          <>
                            <div className='flex gap-4 mb-4'>
                              <SelectField
                                extraClasses='w-full'
                                extraFieldClasses='border !border-gray-200 focus:border-gray-200 focus:ring-gray-400 text-[14px] text-gray-400 placeholder:text-gray-400 !font-normal'
                                onChange={(e) => {
                                  const applicant = applicants[e.target.value]
                                  if (applicant) {
                                    setFieldValue('applicant', {
                                      uuid: applicant.uuid,
                                      first_name: applicant.first_name,
                                      middle_name: applicant.middle_name,
                                      last_name: applicant.last_name,
                                      suffix: applicant.suffix,
                                      social_security_number: applicant.social_security_number,
                                      mailing_address: {
                                        street: applicant.mailing_address.street,
                                        unit_number: applicant.mailing_address.unit_number,
                                        city: applicant.mailing_address.city,
                                        state: applicant.mailing_address.state,
                                        zip_code: applicant.mailing_address.zip_code,
                                      },
                                    })
                                  } else {
                                    setFieldValue('applicant', {
                                      uuid: '',
                                      first_name: '',
                                      middle_name: '',
                                      last_name: '',
                                      suffix: '',
                                      social_security_number: '',
                                      mailing_address: {
                                        street: '',
                                        unit_number: '',
                                        city: '',
                                        state: '',
                                        zip_code: '',
                                      },
                                    })
                                  }
                                  setTimeout(() => {
                                    validateForm()
                                  }, 0)
                                }}
                                label='Select an applicant'
                                name='selectedApplicant'
                                options={applicantsOptions}
                              />
                            </div>
                            {action !== 'unmerge' && (
                              <div>
                                <h4 className='mb-4'>Applicant</h4>
                                <div className='flex gap-2'>
                                  <div className='w-1/5'>
                                    <KeyInputField
                                      placeholder='First Name'
                                      name={`applicant.first_name`}
                                      type='text'
                                      showError={
                                        selectedApplicant !== '' &&
                                        selectedApplicant !== null &&
                                        selectedApplicant !== undefined &&
                                        errors.applicants &&
                                        errors.applicants[selectedApplicant]
                                      }
                                      className='w-full'
                                      disabled={true}
                                    />
                                  </div>
                                  <div className='w-1/5'>
                                    <KeyInputField
                                      placeholder='Middle Name'
                                      name={`applicant.middle_name`}
                                      type='text'
                                      className='w-full'
                                      disabled={true}
                                    />
                                  </div>
                                  <div className='w-1/5'>
                                    <KeyInputField
                                      placeholder='Last Name'
                                      name={`applicant.last_name`}
                                      type='text'
                                      showError={
                                        selectedApplicant !== '' &&
                                        selectedApplicant !== null &&
                                        selectedApplicant !== undefined &&
                                        errors.applicants &&
                                        errors.applicants[selectedApplicant]
                                      }
                                      className='w-full'
                                      disabled={true}
                                    />
                                  </div>
                                  <div className='w-1/5'>
                                    <KeyInputField
                                      placeholder='Suffix'
                                      name={`applicant.suffix`}
                                      type='text'
                                      className='w-full'
                                      disabled={true}
                                    />
                                  </div>
                                  <KeyMaskedSSNInput
                                    key={values.applicant?.uuid}
                                    containerClasses='w-1/5'
                                    placeholder='SSN'
                                    name={`applicant.social_security_number`}
                                    format='###-##-####'
                                    maskedFormat='***-**-####'
                                    profileUuid={values.applicant?.uuid}
                                    type='text'
                                    isNumericString={true}
                                    setFieldValue={setFieldValue}
                                    showError={true}
                                    errors={errors}
                                    touched={touched}
                                    afterOnChange={() => {
                                      setFieldTouched(`applicant.social_security_number`)
                                      setTimeout(() => {
                                        validateForm()
                                      }, 0)
                                    }}
                                    disabled={true}
                                  />
                                </div>
                                <h4 className='my-4'>Residential Address</h4>
                                <div className='flex gap-3 mt-2'>
                                  <div className='w-1/5'>
                                    <KeyInputField
                                      placeholder='Street'
                                      name='applicant.mailing_address.street'
                                      data-test='subject-property-address-input'
                                      showError
                                      className='w-full'
                                      disabled={true}
                                    />
                                  </div>
                                  <div className='w-1/5'>
                                    <KeyInputField
                                      placeholder='Unit Number'
                                      name='applicant.mailing_address.unit_number'
                                      data-test='unit-number-input'
                                      showError
                                      className='w-full'
                                      disabled={true}
                                    />
                                  </div>
                                  <div className='w-1/5'>
                                    <KeyInputField
                                      placeholder='City'
                                      name='applicant.mailing_address.city'
                                      data-test='city-input'
                                      showError
                                      className='w-full'
                                      disabled={true}
                                    />
                                  </div>
                                  <div className='w-1/5'>
                                    <KeyInputField
                                      placeholder='State'
                                      name='applicant.mailing_address.state'
                                      data-test='state-input'
                                      showError
                                      className='w-full'
                                      disabled={true}
                                    />
                                  </div>
                                  <div className='w-1/5'>
                                    <KeyInputField
                                      placeholder='Zip Code'
                                      name='applicant.mailing_address.zip_code'
                                      data-test='zipcode-input'
                                      showError
                                      className='w-full'
                                      disabled={true}
                                    />
                                  </div>
                                </div>
                                <hr className='my-4' />
                              </div>
                            )}
                            {action !== 'unmerge' &&
                              single_or_joint === 'joint' &&
                              coApplicantsOptions.length === 0 && (
                                <>
                                  {!isAddigCoApp ? (
                                    <button
                                      className='btn-primary-legacy btn-sm mt-2'
                                      onClick={() => setIsAddingCoApp(true)}
                                    >
                                      Add Co-Applicant
                                    </button>
                                  ) : (
                                    <div className='flex gap-2'>
                                      <div className='w-1/5'>
                                        <KeyInputField
                                          placeholder='First Name'
                                          name={`newApplicant.first_name`}
                                          type='text'
                                          showError={isAddigCoApp && !!errors.newApplicant}
                                          className='w-full'
                                        />
                                      </div>
                                      <div className='w-1/5'>
                                        <KeyInputField
                                          placeholder='Middle Name'
                                          name={`newApplicant.middle_name`}
                                          type='text'
                                          className='w-full'
                                        />
                                      </div>
                                      <div className='w-1/5'>
                                        <KeyInputField
                                          placeholder='Last Name'
                                          name={`newApplicant.last_name`}
                                          type='text'
                                          showError={isAddigCoApp && !!errors.newApplicant}
                                          className='w-full'
                                        />
                                      </div>
                                      <div className='w-1/5'>
                                        <KeyInputField
                                          placeholder='Suffix'
                                          name={`newApplicant.suffix`}
                                          type='text'
                                          className='w-full'
                                        />
                                      </div>
                                      <KeyMaskedSSNInput
                                        containerClasses='w-1/5'
                                        placeholder='SSN'
                                        name={`newApplicant.social_security_number`}
                                        format='###-##-####'
                                        maskedFormat='***-**-####'
                                        profile={null}
                                        type='text'
                                        isNumericString={true}
                                        setFieldValue={setFieldValue}
                                        showError={true}
                                        errors={errors}
                                        touched={touched}
                                        afterOnChange={() => {
                                          setFieldTouched('newApplicant.social_security_number')
                                          setTimeout(() => {
                                            validateForm()
                                          }, 0)
                                        }}
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                            {((single_or_joint === 'joint' && coApplicantsOptions.length > 0) ||
                              action === 'unmerge') && (
                              <div className='flex gap-4 mb-4'>
                                <SelectField
                                  label='Select a co-applicant'
                                  name='selectedCoApplicant'
                                  extraClasses='w-full'
                                  extraFieldClasses='border !border-gray-200 focus:border-gray-200 focus:ring-gray-400 text-[14px] text-gray-400 placeholder:text-gray-400 !font-normal'
                                  options={coApplicantsOptions}
                                  onChange={(e) => {
                                    const applicant = applicants[e.target.value]
                                    if (applicant) {
                                      setFieldValue('coApplicant', {
                                        uuid: applicant.uuid,
                                        first_name: applicant.first_name,
                                        middle_name: applicant.middle_name,
                                        last_name: applicant.last_name,
                                        suffix: applicant.suffix,
                                        social_security_number: applicant.social_security_number,
                                        mailing_address: {
                                          street: applicant.mailing_address.street,
                                          unit_number: applicant.mailing_address.unit_number,
                                          city: applicant.mailing_address.city,
                                          state: applicant.mailing_address.state,
                                          zip_code: applicant.mailing_address.zip_code,
                                        },
                                      })
                                    } else {
                                      setFieldValue('coApplicant', {
                                        uuid: '',
                                        first_name: '',
                                        middle_name: '',
                                        last_name: '',
                                        suffix: '',
                                        social_security_number: '',
                                        mailing_address: {
                                          street: '',
                                          unit_number: '',
                                          city: '',
                                          state: '',
                                          zip_code: '',
                                        },
                                      })
                                    }
                                    if (action === 'unmerge') {
                                      setFieldValue(
                                        'mailing_address',
                                        applicants[e.target.value] ??
                                          applicants[e.target.value].mailing_address
                                      )
                                    }
                                    setTimeout(() => {
                                      validateForm()
                                    }, 0)
                                  }}
                                />
                              </div>
                            )}
                            {selectedApplicant !== '' &&
                              selectedApplicant !== null &&
                              selectedApplicant !== undefined &&
                              single_or_joint == 'joint' &&
                              action && (
                                <>
                                  <h4 className='mb-4'>
                                    {selectedApplicant !== '' &&
                                    selectedApplicant !== null &&
                                    selectedApplicant !== undefined &&
                                    applicants[selectedApplicant] &&
                                    applicants[selectedApplicant].uuid === applicant.profile.uuid
                                      ? `Applicant`
                                      : `Co-Applicant`}
                                  </h4>
                                  <div className='flex gap-2'>
                                    <div className='w-1/5'>
                                      <KeyInputField
                                        placeholder='First Name'
                                        name={`coApplicant.first_name`}
                                        type='text'
                                        showError={
                                          selectedApplicant !== '' &&
                                          selectedApplicant !== null &&
                                          selectedApplicant !== undefined &&
                                          errors.applicants &&
                                          errors.applicants[selectedApplicant]
                                        }
                                        className='w-full'
                                        disabled={true}
                                      />
                                    </div>
                                    <div className='w-1/5'>
                                      <KeyInputField
                                        placeholder='Middle Name'
                                        name={`coApplicant.middle_name`}
                                        type='text'
                                        className='w-full'
                                        disabled={true}
                                      />
                                    </div>
                                    <div className='w-1/5'>
                                      <KeyInputField
                                        placeholder='Last Name'
                                        name={`coApplicant.last_name`}
                                        type='text'
                                        showError={
                                          selectedApplicant !== '' &&
                                          selectedApplicant !== null &&
                                          selectedApplicant !== undefined &&
                                          errors.applicants &&
                                          errors.applicants[selectedApplicant]
                                        }
                                        className='w-full'
                                        disabled={true}
                                      />
                                    </div>
                                    <div className='w-1/5'>
                                      <KeyInputField
                                        placeholder='Suffix'
                                        name={`coApplicant.suffix`}
                                        type='text'
                                        className='w-full'
                                        disabled={true}
                                      />
                                    </div>
                                    <KeyMaskedSSNInput
                                      key={values.coApplicant?.uuid}
                                      containerClasses='w-1/5'
                                      placeholder='SSN'
                                      name={`coApplicant.social_security_number`}
                                      format='###-##-####'
                                      maskedFormat='***-**-####'
                                      profileUuid={values.coApplicant?.uuid}
                                      type='text'
                                      isNumericString={true}
                                      setFieldValue={setFieldValue}
                                      showError={true}
                                      errors={errors}
                                      touched={touched}
                                      afterOnChange={() => {
                                        setFieldTouched(`coApplicant.social_security_number`)
                                        setTimeout(() => {
                                          validateForm()
                                        }, 0)
                                      }}
                                      disabled={true}
                                    />
                                  </div>
                                  {single_or_joint == 'joint' && (
                                    <>
                                      <h4 className='my-4'>Residential Address</h4>
                                      <div className='flex gap-3 mt-2'>
                                        <div className='w-1/5'>
                                          <KeyInputField
                                            placeholder='Street'
                                            name='coApplicant.mailing_address.street'
                                            data-test='subject-property-address-input'
                                            showError
                                            className='w-full'
                                            disabled={true}
                                          />
                                        </div>
                                        <div className='w-1/5'>
                                          <KeyInputField
                                            placeholder='Unit Number'
                                            name='coApplicant.mailing_address.unit_number'
                                            data-test='unit-number-input'
                                            showError
                                            className='w-full'
                                            disabled={true}
                                          />
                                        </div>
                                        <div className='w-1/5'>
                                          <KeyInputField
                                            placeholder='City'
                                            name='coApplicant.mailing_address.city'
                                            data-test='city-input'
                                            showError
                                            className='w-full'
                                            disabled={true}
                                          />
                                        </div>
                                        <div className='w-1/5'>
                                          <KeyInputField
                                            placeholder='State'
                                            name='coApplicant.mailing_address.state'
                                            data-test='state-input'
                                            showError
                                            className='w-full'
                                            disabled={true}
                                          />
                                        </div>
                                        <div className='w-1/5'>
                                          <KeyInputField
                                            placeholder='Zip Code'
                                            name='coApplicant.mailing_address.zip_code'
                                            data-test='zipcode-input'
                                            showError
                                            className='w-full'
                                            disabled={true}
                                          />
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </>
                              )}
                          </>
                        )}
                      </div>
                    )}
                    {action && (
                      <div className='flex justify-between items-end pt-10 p-4'>
                        <button
                          className='btn btn-primary btn-sm'
                          type='submit'
                          disabled={!isValid}
                        >
                          Order Report
                        </button>
                      </div>
                    )}
                  </Form>
                </div>
              )}
            </div>
          </div>
        )
      }}
    </Formik>
  )
}
